import type React from "react";
import { lazy, useCallback, useLayoutEffect, useRef, useState } from "react";
import { LazyChunkBoundary } from "@design-system/LazyChunkBoundary/LazyChunkBoundary";
import { styleWithVars } from "@lib/cssVars";
import type { BarTap } from "./DensityChart.lib";
import {
    BAR_GAP_SHARE,
    barTapAtPoint,
    formatDensityPeriod,
    PLOT_HEIGHT,
} from "./DensityChart.lib";
import { bucketToWindow } from "./densityBuckets.lib";
import {
    BAR_PITCH_PX,
    EARLIER_FADE_PX,
    TODAY_INSET_PX,
} from "./densityViewport.lib";
import styles from "./DensityChart.module.scss";
import { DensityChartAxis } from "./DensityChartAxis";
import { DensityChartBeforeStart } from "./DensityChartBeforeStart";
import { DensityChartPanControls } from "./DensityChartPanControls";
import type { DensityChartProps } from "./DensityChartProps";
import { DensityChartOpenSlice } from "./DensityChartSliceTooltip";
import { DensityChartTracks } from "./DensityChartTracks";
import { DensityChartViewport } from "./DensityChartViewport";
import { DensityChartWindowOverlay } from "./DensityChartWindowOverlay";
import { useAxisLabelCovers } from "./useAxisLabelCovers";
import { useBarHover } from "./useBarHover";
import { useDensityStrip } from "./useDensityStrip";
import type { HoveredSlice } from "./useSliceHover";
import { useSliceHover } from "./useSliceHover";

// Keeps @tanstack/charts and its d3 packages (~38 kB gzip) out of the chunk that renders the chart.
const DensityChartPlot = lazy(() =>
    import("./DensityChartPlot").then((module) => ({
        default: module.DensityChartPlot,
    })),
);

/**
 * A timeline of stacked bars, one per day, week or month, from `startDate`
 * to today on a fixed pitch. It opens on the window and pans with arrows or
 * a sideways scroll. Each segment has a hover tooltip, and the window can be
 * dragged. Outlier bars are clipped instead of using a log scale. Every bar
 * has a full-height track, and hovering an empty one says it has no
 * activity, that it's loading, or that it failed to load. The hovered
 * segment, or a bar's track hovered off its segments, draws darker.
 */
export function DensityChart({
    ariaLabel,
    startDate,
    endDate,
    bucketUnit = "day",
    days,
    loadedDays,
    failedDays,
    staleDays,
    series,
    window,
    onWindowChange,
    onWindowPreview,
    onShownBucketUnitChange,
    onDrawnDaysChange,
    onSliceClick,
    onBarClick,
    highlight,
    formatPeriod = formatDensityPeriod,
    isLoading = false,
    beforeStartMessage,
}: DensityChartProps) {
    const viewportRef = useRef<HTMLDivElement>(null);
    const plotAreaRef = useRef<HTMLDivElement>(null);
    const [chartRoot, setChartRoot] = useState<HTMLElement | null>(null);
    const { openSlice, hoverSlice, release, close } = useSliceHover();
    const [hoveredBar, setHoveredBar] = useState<HoveredSlice | null>(null);
    const closeHover = useCallback(() => {
        close();
        setHoveredBar(null);
    }, [close]);
    const {
        unit,
        viewport,
        isEasing,
        pan,
        dragStrip,
        visible,
        buckets,
        zoneCount,
        minBar,
        segments,
        staleBucketStarts,
        busyBucketStarts,
        dayByDate,
        isBarLoaded,
        isBarFailed,
        windowSpan,
        keepPicked,
        pickWindow,
        previewWindow,
    } = useDensityStrip({
        viewportRef,
        root: chartRoot,
        startDate,
        endDate,
        closestUnit: bucketUnit,
        window,
        days,
        loadedDays,
        failedDays,
        staleDays,
        series,
        onPan: closeHover,
        onWindowChange,
        onWindowPreview,
        onShownUnitChange: onShownBucketUnitChange,
        onDrawnDaysChange,
    });
    useLayoutEffect(() => closeHover, [buckets, closeHover]);
    useAxisLabelCovers({
        root: chartRoot,
        motionKey: [
            unit,
            viewport.firstBar,
            viewport.visibleStart,
            buckets.length,
            windowSpan?.start,
            windowSpan?.end,
        ].join("|"),
    });
    const hasWindow = window !== undefined && onWindowChange !== undefined;

    const toEmptyMessage = (index: number) => {
        if (isBarLoaded(index)) {
            return "No activity";
        }
        return isBarFailed(index) ? "Failed to load activity" : "Loading…";
    };
    const { seriesAt, handlePointerMove, handlePointerLeave } = useBarHover({
        plotAreaRef,
        buckets,
        zoneCount,
        segments,
        busyBucketStarts,
        isLoading,
        hoverSlice,
        setHoveredBar,
    });
    const hoveredSeriesId = hoveredBar?.seriesId ?? undefined;
    const hoveredSegmentBucket =
        hoveredBar && hoveredSeriesId
            ? buckets[hoveredBar.bucketIndex]?.start
            : undefined;

    const handleTap = ({ barIndex, y }: BarTap) => {
        const bucket = buckets[barIndex];
        if (!bucket || barIndex < zoneCount) {
            return;
        }
        close();
        const seriesId = onSliceClick ? seriesAt({ bucket, y }) : null;
        const tapped = keepPicked(bucketToWindow(bucket));
        if (seriesId !== null) {
            onSliceClick?.({ seriesId, window: tapped });
        } else if (onBarClick) {
            onBarClick({ window: tapped });
        } else {
            onWindowChange?.(tapped);
        }
    };
    const handlePlotClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const rect = plotAreaRef.current?.getBoundingClientRect();
        if (!rect) {
            return;
        }
        handleTap(
            barTapAtPoint({ rect, point: event, barCount: buckets.length }),
        );
    };

    return (
        <section
            ref={setChartRoot}
            className={styles.root}
            style={styleWithVars({
                "--plot-height": `${PLOT_HEIGHT}px`,
                "--bar-pitch": `${BAR_PITCH_PX}px`,
                "--today-inset": `${TODAY_INSET_PX}px`,
                "--earlier-fade-width": `${EARLIER_FADE_PX}px`,
                "--zone-share": zoneCount / Math.max(buckets.length, 1),
            })}
        >
            <DensityChartViewport
                viewportRef={viewportRef}
                viewport={viewport}
                barCount={buckets.length}
                isEasing={isEasing}
                onPointerMove={handlePointerMove}
                onPointerLeave={() => {
                    release();
                    handlePointerLeave();
                }}
            >
                <div
                    ref={plotAreaRef}
                    className={styles.plotArea}
                    onClick={hasWindow ? undefined : handlePlotClick}
                >
                    {!isLoading && openSlice && (
                        <DensityChartOpenSlice
                            slice={openSlice}
                            buckets={buckets}
                            series={series}
                            dayByDate={dayByDate}
                            formatPeriod={formatPeriod}
                            emptyMessage={toEmptyMessage(openSlice.bucketIndex)}
                            plotWidth={buckets.length * BAR_PITCH_PX}
                            visible={visible}
                            container={chartRoot}
                        />
                    )}
                    <DensityChartTracks
                        count={buckets.length - zoneCount}
                        gapShare={BAR_GAP_SHARE}
                        className={styles.realTracks}
                        hoveredIndex={
                            hoveredBar && hoveredBar.seriesId === null
                                ? hoveredBar.bucketIndex - zoneCount
                                : undefined
                        }
                    />
                    {!isLoading && (
                        <LazyChunkBoundary fallback={null}>
                            <DensityChartPlot
                                key={unit}
                                ariaLabel={ariaLabel}
                                buckets={buckets}
                                segments={segments}
                                staleBucketStarts={staleBucketStarts}
                                highlight={highlight}
                                hoveredBucket={hoveredSegmentBucket}
                                hoveredSeriesId={hoveredSeriesId}
                                series={series}
                                gapShare={BAR_GAP_SHARE}
                                zoneCount={zoneCount}
                            />
                        </LazyChunkBoundary>
                    )}
                </div>
                <DensityChartAxis
                    buckets={buckets}
                    unit={unit}
                    endDate={endDate}
                    visibleStart={viewport.visibleStart}
                />
                {hasWindow && windowSpan && zoneCount < buckets.length && (
                    <DensityChartWindowOverlay
                        buckets={buckets}
                        windowSpan={windowSpan}
                        minBar={minBar}
                        strip={dragStrip}
                        visible={visible}
                        onWindowChange={pickWindow}
                        onWindowPreview={previewWindow}
                        onTap={handleTap}
                        formatPeriod={formatPeriod}
                    />
                )}
                {zoneCount > 0 && (
                    <DensityChartBeforeStart
                        zoneCount={zoneCount}
                        bucketCount={buckets.length}
                        gapShare={BAR_GAP_SHARE}
                        message={beforeStartMessage}
                    />
                )}
            </DensityChartViewport>
            <DensityChartPanControls
                canPanEarlier={viewport.canPanEarlier}
                canPanLater={viewport.canPanLater}
                earlierFade={viewport.earlierFade}
                onPan={pan}
            />
        </section>
    );
}
